import React from "react";
import { Button, Modal } from "../../../components/ui";
import { formatCreatedOn } from "../../brand/planFormat";

interface CancelSubscriptionModalProps {
  open: boolean;
  planName: string;
  /** The day the current cycle is paid up to, as an ISO date. */
  paidUntil: string | null;
  onClose: () => void;
  onConfirm: () => void;
  cancelling?: boolean;
}

/**
 * Stopping the subscription.
 *
 * It cancels at the end of the cycle, and says so plainly, because the
 * alternative is taking away something already paid for. That also answers the
 * question anyone hovering over Cancel is actually asking — "do I lose it right
 * now?" — which is what stops the cancellation being a support ticket.
 *
 * There is no "are you sure" beyond this: the action is reversible by
 * subscribing again, and stacking a second confirmation on a decision somebody
 * has already made twice is nagging, not safety.
 */
const CancelSubscriptionModal: React.FC<CancelSubscriptionModalProps> = ({
  open,
  planName,
  paidUntil,
  onClose,
  onConfirm,
  cancelling = false,
}) => (
  <Modal
    open={open}
    title="Cancel your plan"
    onClose={() => {
      // A cancellation in flight must not be dismissable: the request has gone
      // and the answer decides what the screen behind this says.
      if (!cancelling) onClose();
    }}
    className="max-w-[460px]"
    footer={
      <>
        <Button
          variant="secondary"
          size="md"
          disabled={cancelling}
          onClick={onClose}
        >
          Keep my plan
        </Button>
        <Button
          variant="danger"
          size="md"
          loading={cancelling}
          onClick={onConfirm}
        >
          Cancel plan
        </Button>
      </>
    }
  >
    <p className="text-[14px] 3xl:text-[15px] leading-relaxed text-ink-muted">
      Auto-renewal will be turned off, but your current {planName} subscription
      will remain active until{" "}
      {paidUntil ? (
        <span className="font-medium text-ink">
          {formatCreatedOn(new Date(paidUntil))}
        </span>
      ) : (
        <>the end of the cycle you have paid for</>
      )}
      . You won&rsquo;t be charged for the next billing cycle.
    </p>
    <p className="mt-3 text-[14px] 3xl:text-[15px] leading-relaxed text-ink-muted">
      You&rsquo;ll continue to have full access to your clients, plans, and
      records until your current subscription ends. You can subscribe again
      anytime and pick up where you left off.
    </p>
  </Modal>
);

export default CancelSubscriptionModal;
